import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAdsQuery, useDeleteAdMutation, useGetAdTypesQuery, useGetAdCategoriesQuery, useBulkAdActionMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import { getStatusColor, formatCurrency } from '../../../utils/helpers';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const ADS_FILTER_KEY = 'admin_ads_filters';
function readAdsFilters() {
  try { return JSON.parse(sessionStorage.getItem(ADS_FILTER_KEY) || '{}'); } catch { return {}; }
}

const AdminAds: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>(() => readAdsFilters().search ?? '');
  const [daterange, setDaterange] = useState<string>(() => readAdsFilters().daterange ?? '');
  const [adCategoryId, setAdCategoryId] = useState<number | ''>(() => readAdsFilters().adCategoryId ?? '');
  const [adTypeId, setAdTypeId] = useState<number | ''>(() => readAdsFilters().adTypeId ?? '');
  const [status, setStatus] = useState<string>(() => readAdsFilters().status ?? '');
  const [page, setPage] = useState<number>(() => readAdsFilters().page ?? 1);
  const [activeTab, setActiveTab] = useState<string>(() => readAdsFilters().activeTab ?? 'All');

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkRejectionReason, setBulkRejectionReason] = useState<string>('');

  // Persist filters across navigation
  useEffect(() => {
    sessionStorage.setItem(ADS_FILTER_KEY, JSON.stringify({ search, daterange, adCategoryId, adTypeId, status, page, activeTab }));
  }, [search, daterange, adCategoryId, adTypeId, status, page, activeTab]);

  const { data: adTypesData } = useGetAdTypesQuery({});
  const { data: adCategoriesData } = useGetAdCategoriesQuery({});

  const adTypes = adTypesData?.data?.data || [];
  const adCategories = adCategoriesData?.data?.data || [];

  const tabs = [
    { id: 'All', title: 'All Ads' },
    ...adTypes.map((type: any) => ({ id: type.id, title: type.title }))
  ];

  const { data, isLoading, error, refetch } = useGetAdsQuery({
    search,
    daterange,
    ad_category_id: adCategoryId || undefined,
    ad_type_id: activeTab !== 'All' ? activeTab : undefined,
    status: status !== '' ? status : undefined,
    page
  });

  const [deleteAd] = useDeleteAdMutation();
  const [bulkAdAction, { isLoading: isBulkLoading }] = useBulkAdActionMutation();

  useEffect(() => {
    setPage(1);
  }, [search, daterange, adCategoryId, activeTab, status]);

  // Clear selection on page/filter change
  useEffect(() => {
    setSelectedIds([]);
  }, [page, search, daterange, adCategoryId, activeTab, status]);

  const ads = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };
  const pageIds: number[] = ads.data.map((ad: any) => ad.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...prev, ...pageIds.filter((id) => !prev.includes(id))]);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteAd(id).unwrap();
      showSuccess(result.message || 'Ad deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete ad. Please try again.');
    }
  };

  const handleBulkAction = async () => {
    if (!bulkStatus) return showError('Please select a status to apply.');
    if (selectedIds.length === 0) return showError('Please select at least one ad.');
    try {
      const payload: any = { ids: selectedIds, action: bulkStatus };
      if (bulkStatus === 'Rejected' && bulkRejectionReason.trim()) {
        payload.rejection_reason = bulkRejectionReason.trim();
      }
      const result = await bulkAdAction(payload).unwrap();
      showSuccess(result.message || 'Bulk action applied!');
      setSelectedIds([]);
      setBulkStatus('');
      setBulkRejectionReason('');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Bulk action failed. Please try again.');
    }
  };

  const handleClear = () => {
    setSearch('');
    setDaterange('');
    setAdCategoryId('');
    setAdTypeId('');
    setStatus('');
    setActiveTab('All');
    setPage(1);
  };

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">Error loading ads</div>;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Ads Management</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Ads</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title w-100 flex-column align-items-start gap-4">
                  {/* Tabs */}
                  <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold mb-4">
                    {tabs.map((tab) => (
                      <li className="nav-item" key={tab.id}>
                        <a
                          className={`nav-link text-active-primary ms-0 me-10 py-5 ${activeTab === tab.id ? 'active' : ''}`}
                          href="#"
                          onClick={(e) => { e.preventDefault(); setActiveTab(tab.id); }}
                        >
                          {tab.title}
                        </a>
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={(e) => e.preventDefault()} className="w-100">
                    <div className="d-flex align-items-center position-relative my-1 flex-wrap gap-3">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-250px ps-15"
                        placeholder="Search Ad Title.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <DateRangePicker value={daterange} onChange={setDaterange} placeholder="Date Range" />
                      <select
                        className="form-select form-select-solid w-200px"
                        value={adCategoryId}
                        onChange={(e) => setAdCategoryId(e.target.value ? Number(e.target.value) : '')}
                      >
                        <option value="">All Categories</option>
                        {adCategories.map((category: any) => (
                          <option key={category.id} value={category.id}>{category.title}</option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-solid w-200px"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Active">Active</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Expired">Expired</option>
                        <option value="Draft">Draft</option>
                      </select>
                      {(search || daterange || status !== '' || adCategoryId !== '' || activeTab !== 'All') && (
                        <button type="button" className="btn btn-sm btn-light-primary" onClick={handleClear}>
                          Clear
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              {/* Bulk action toolbar — visible only when rows are selected */}
              {someSelected && (
                <div className="card-header border-0 pt-0 pb-4">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span className="text-gray-700 fw-semibold fs-6">
                      {selectedIds.length} ad{selectedIds.length > 1 ? 's' : ''} selected
                    </span>
                    <select
                      className="form-select form-select-solid form-select-sm w-175px"
                      value={bulkStatus}
                      onChange={(e) => { setBulkStatus(e.target.value); setBulkRejectionReason(''); }}
                    >
                      <option value="">Change Status…</option>
                      <option value="Active">Active</option>
                      <option value="Rejected">Rejected</option>
                      <option value="delete">Delete</option>
                    </select>
                    {bulkStatus === 'Rejected' && (
                      <input
                        type="text"
                        className="form-control form-control-solid form-control-sm w-250px"
                        placeholder="Rejection reason (optional)"
                        value={bulkRejectionReason}
                        onChange={(e) => setBulkRejectionReason(e.target.value)}
                      />
                    )}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleBulkAction}
                      disabled={isBulkLoading || !bulkStatus}
                    >
                      {isBulkLoading ? 'Applying…' : 'Apply'}
                    </button>
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => { setSelectedIds([]); setBulkStatus(''); setBulkRejectionReason(''); }}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
              )}

              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-30px pe-2">
                          <div className="form-check form-check-sm form-check-custom form-check-solid">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              title={allSelected ? 'Deselect all on this page' : 'Select all on this page'}
                            />
                          </div>
                        </th>
                        <th>AD ID</th>
                        <th>TITLE</th>
                        <th>CATEGORY</th>
                        <th>TYPE</th>
                        <th>USER</th>
                        <th>LANGUAGE</th>
                        <th>STATUS</th>
                        <th>VIEWS</th>
                        <th>CLICKS</th>
                        <th>PAID AMOUNT</th>
                        <th>CREATED DATE</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {ads.data.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="text-center py-10">
                            <span className="text-muted">No ads found</span>
                          </td>
                        </tr>
                      ) : (
                        ads.data.map((ad: any) => (
                          <tr
                            key={ad.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/admin/ads/${ad.id}`)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="form-check form-check-sm form-check-custom form-check-solid">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedIds.includes(ad.id)}
                                  onChange={() => toggleSelect(ad.id)}
                                />
                              </div>
                            </td>
                            <td>
                              <span className="text-gray-800 fw-bold">{ad.ad_id || ad.id}</span>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800 fw-bold">{ad.title || 'N/A'}</span>
                                {ad.description && (
                                  <span className="text-muted fs-7" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {ad.description}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge badge-light-primary">{ad.ad_category?.title || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="badge badge-light-info">{ad.ad_type?.title || 'N/A'}</span>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800 fw-bold">{ad.user?.name || 'N/A'}</span>
                                {ad.user?.email && <span className="text-muted fs-7">{ad.user.email}</span>}
                              </div>
                            </td>
                            <td>
                              <span className="badge badge-light-success">{ad.ad_language?.toUpperCase() || 'EN'}</span>
                            </td>
                            <td>
                              <span className={`badge ${getStatusColor(ad.status)}`}>{ad.status || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="text-gray-800 fw-bold">{ad.ad_stat?.views ?? ad.views ?? 0}</span>
                            </td>
                            <td>
                              <span className="text-gray-800 fw-bold">{ad.ad_stat?.clicks ?? ad.clicks ?? 0}</span>
                            </td>
                            <td>
                              <span className="text-gray-800 fw-bold">{formatCurrency(ad.payment_amount)}</span>
                            </td>
                            <td>{formatDate(ad.created_at)}</td>
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <ActionButtons
                                onView={() => navigate(`/admin/ads/${ad.id}`)}
                                onDelete={() => handleDelete(ad.id)}
                                viewTitle="View Details"
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {ads.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={ads.current_page}
                      lastPage={ads.last_page}
                      total={ads.total || 0}
                      perPage={ads.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAds;
